import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ArrowRightIcon, XCloseIcon } from '@untitledui-icons/react/line'

interface WhatsAppHubBannerProps {
  onDismiss: () => void
}

/**
 * App-wide promo bar pointing at the WhatsApp hub. Sticky rather than static so
 * it stays in view while a page scrolls; the fixed sidebar and the mobile header
 * are offset by its height in AppLayout.
 */
export function WhatsAppHubBanner({ onDismiss }: WhatsAppHubBannerProps) {
  const { t } = useTranslation()
  const base = 'components.whatsapp_hub_banner'

  return (
    <div className="sticky top-0 z-40 h-10 shrink-0 bg-gradient-to-r from-[#1C1917] to-[#44403C] text-white">
      <Link
        to="/widgets"
        aria-label={t(`${base}.cta`)}
        className="flex h-full items-center justify-center gap-6 pr-12 pl-4 outline-hidden focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset"
      >
        <div className="flex min-w-0 items-center gap-2">
          <WhatsAppBadge className="size-6 shrink-0" />
          <p className="truncate text-sm font-medium">{t(`${base}.message`)}</p>
        </div>

        <span className="hidden shrink-0 items-center gap-2 text-sm font-medium underline underline-offset-2 sm:flex">
          {t(`${base}.cta`)}
          <ArrowRightIcon className="size-4" />
        </span>
      </Link>

      <button
        type="button"
        onClick={onDismiss}
        aria-label={t(`${base}.dismiss`)}
        className="absolute top-1.5 right-3 z-10 flex size-7 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white"
      >
        <XCloseIcon className="size-4" />
      </button>
    </div>
  )
}

/**
 * Official WhatsApp glyph (same path as the conversations channel icon) on its
 * brand-green tile, 24px with a 4px radius per the design.
 */
function WhatsAppBadge({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect width="24" height="24" rx="4" fill="#25D366" />
      <g transform="translate(3.6 3.6) scale(0.7)">
        <path
          fill="#ffffff"
          d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
        />
      </g>
    </svg>
  )
}

export default WhatsAppHubBanner
